import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Calendar, MapPin, Clock, Loader2, UserPlus, Share2, ExternalLink, ChevronDown, ChevronUp } from "lucide-react";
import { Link } from "react-router-dom";
import { format, differenceInDays, isPast } from "date-fns";
import { parseLocalDate } from "@/lib/utils";
import EventInviteDialog from "@/components/EventInviteDialog";
import SocialSharePanel from "@/components/SocialSharePanel";
import NestedChallengesSection from "@/components/dashboard/NestedChallengesSection";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const PRODUCTION_DOMAIN = "https://buildwithchrist.ai";

interface EventWithRegistration {
  id: string;
  name: string;
  event_date: string;
  event_time: string | null;
  location: string;
  description: string | null;
  slug: string;
  registered_at: string;
  attendee_type: string | null;
}

const UserEvents: React.FC = () => {
  const [showPastEvents, setShowPastEvents] = useState(false);

  const { data: events, isLoading, error } = useQuery({
    queryKey: ["user-registered-events"],
    staleTime: 30000,
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data: registrations, error } = await supabase
        .from("event_registrations")
        .select(`
          id,
          created_at,
          attendee_type,
          event_id,
          events!event_id (
            id,
            name,
            event_date,
            event_time,
            location,
            description,
            slug
          )
        `)
        .eq("user_id", user.id)
        .not("event_id", "is", null)
        .order("created_at", { ascending: false });

      if (error) throw error;

      const eventsWithRegistration: EventWithRegistration[] = [];
      const seenEventIds = new Set<string>();

      for (const reg of registrations || []) {
        const event = reg.events;
        if (event && !seenEventIds.has(event.id)) {
          seenEventIds.add(event.id);
          eventsWithRegistration.push({
            id: event.id,
            name: event.name,
            event_date: event.event_date,
            event_time: event.event_time,
            location: event.location,
            description: event.description,
            slug: event.slug,
            registered_at: reg.created_at,
            attendee_type: reg.attendee_type,
          });
        }
      }

      return eventsWithRegistration;
    },
  });

  if (isLoading) {
    return (
      <div className="p-4 md:p-6 lg:p-8">
        <div className="mb-6">
          <Skeleton className="h-8 w-32 mb-2" />
          <Skeleton className="h-4 w-64" />
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 md:p-6 lg:p-8">
        <Card className="border-destructive">
          <CardContent className="py-8 text-center">
            <p className="text-destructive">Failed to load your events. Please try again.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const upcomingEvents = events?.filter(e => parseLocalDate(e.event_date) >= today) || [];
  const pastEvents = events?.filter(e => parseLocalDate(e.event_date) < today) || [];

  // Get the featured event (nearest upcoming)
  const featuredEvent = upcomingEvents[0];
  const otherUpcomingEvents = upcomingEvents.slice(1);

  return (
    <div className="p-4 md:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground">My Events</h1>
            <p className="text-muted-foreground mt-1 text-sm">
              Events you've registered for
            </p>
          </div>
          <Button variant="outline" asChild>
            <Link to="/events">
              Browse All Events
            </Link>
          </Button>
        </div>
      </div>

      {events?.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <CardTitle className="mb-2">No Events Yet</CardTitle>
            <CardDescription className="mb-4">
              You haven't registered for any events. Find your first event!
            </CardDescription>
            <Button asChild>
              <Link to="/events">Find Your First Event</Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Featured Upcoming Event */}
          {featuredEvent && (
            <FeaturedEventCard event={featuredEvent} />
          )}

          {/* Other Upcoming Events */}
          {otherUpcomingEvents.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold mb-3 text-foreground">
                More Upcoming Events
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {otherUpcomingEvents.map((event) => (
                  <CompactEventCard key={event.id} event={event} />
                ))}
              </div>
            </div>
          )}

          {/* Past Events - Collapsed by default */}
          {pastEvents.length > 0 && (
            <div>
              <button
                onClick={() => setShowPastEvents(!showPastEvents)}
                className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                {showPastEvents ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
                Past Events ({pastEvents.length})
              </button>
              
              {showPastEvents && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-3 opacity-75">
                  {pastEvents.map((event) => (
                    <CompactEventCard key={event.id} event={event} isPast />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Featured Event Card - Hero treatment for nearest upcoming event
const FeaturedEventCard: React.FC<{ event: EventWithRegistration }> = ({ event }) => {
  const eventDate = parseLocalDate(event.event_date);
  const daysUntil = differenceInDays(eventDate, new Date());
  const eventUrl = `${PRODUCTION_DOMAIN}/events/${event.slug}`;

  const getCountdownBadge = () => {
    if (daysUntil === 0) {
      return <Badge className="bg-secondary hover:bg-secondary/90">Today!</Badge>;
    }
    if (daysUntil === 1) {
      return <Badge className="bg-primary hover:bg-primary/90">Tomorrow</Badge>;
    }
    if (daysUntil <= 7) {
      return <Badge className="bg-primary/80 hover:bg-primary/70">{daysUntil} days away</Badge>;
    }
    return <Badge variant="outline">{daysUntil} days away</Badge>;
  };

  return (
    <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-transparent">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="space-y-1 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="secondary" className="text-xs">Featured</Badge>
              {getCountdownBadge()}
            </div>
            <CardTitle className="text-xl md:text-2xl mt-2">{event.name}</CardTitle>
            {event.attendee_type && (
              <Badge variant="outline" className="text-xs mt-1">
                {event.attendee_type}
              </Badge>
            )}
          </div>
        </div>
        {event.description && (
          <CardDescription className="line-clamp-2 mt-2">
            {event.description}
          </CardDescription>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <Calendar className="h-4 w-4" />
            <span>{format(eventDate, "EEEE, MMMM d, yyyy")}</span>
          </div>
          {event.event_time && (
            <div className="flex items-center gap-1.5">
              <Clock className="h-4 w-4" />
              <span>{event.event_time}</span>
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <MapPin className="h-4 w-4" />
            <span>{event.location}</span>
          </div>
        </div>

        {/* Primary CTA - Invite Friends */}
        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <EventInviteDialog
            eventId={event.id}
            eventName={event.name}
            eventSlug={event.slug}
            eventDescription={event.description || undefined}
            trigger={
              <Button className="w-full sm:w-auto">
                <UserPlus className="h-4 w-4 mr-2" />
                Invite a Friend
              </Button>
            }
          />
          
          <div className="flex gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="default">
                  <Share2 className="h-4 w-4 mr-2" />
                  Share
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-4" align="start">
                <SocialSharePanel
                  type="event"
                  title={event.name}
                  url={eventUrl}
                  description={event.description || undefined}
                />
              </PopoverContent>
            </Popover>
            
            <Button
              variant="ghost"
              onClick={() => window.open(`/events/${event.slug}`, "_blank")}
            >
              <ExternalLink className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Nested Challenges Section */}
        <NestedChallengesSection eventId={event.id} />
      </CardContent>
    </Card>
  );
};

// Compact Event Card - For other upcoming and past events
const CompactEventCard: React.FC<{ event: EventWithRegistration; isPast?: boolean }> = ({ 
  event, 
  isPast = false 
}) => {
  const eventDate = parseLocalDate(event.event_date);

  return (
    <Card className={cn(
      "hover:border-primary/50 transition-colors",
      isPast && "opacity-75"
    )}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-medium text-foreground line-clamp-1">{event.name}</h3>
          {isPast && <Badge variant="secondary" className="shrink-0">Past</Badge>}
        </div>
        
        <div className="flex flex-wrap gap-3 text-xs text-muted-foreground mb-3">
          <div className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            <span>{format(eventDate, "MMM d, yyyy")}</span>
          </div>
          <div className="flex items-center gap-1">
            <MapPin className="h-3 w-3" />
            <span className="truncate">{event.location}</span>
          </div>
        </div>
        
        {!isPast && (
          <div className="flex gap-2">
            <EventInviteDialog
              eventId={event.id}
              eventName={event.name}
              eventSlug={event.slug}
              eventDescription={event.description || undefined}
              trigger={
                <Button variant="outline" size="sm">
                  <UserPlus className="h-3.5 w-3.5 mr-1" />
                  Invite
                </Button>
              }
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.open(`/events/${event.slug}`, "_blank")}
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default UserEvents;
